export const ADMIN_SCOPE_VALUES = {
  GENERAL: 'general',
  PF: 'pf',
  DIPLOM: 'diplom',
  NDN: 'ndn',
  CS: 'cs',
  COP: 'cop'
};

export const COP_SCHOOL_TYPE = 'DEPEN';

export const COP_MEETING_TYPES = [
  'COP_SOLICITUD',
  'COP_PLANEACION',
  'COP_DESARROLLO',
  'COP_CIERRE'
];

const NDN_MEDIATOR_ROLE_ID = 3;

export const MEDELLIN_COMMUNE_IDS = [
  1, 2, 3, 4, 5, 6, 7, 8,
  9, 10, 11, 12, 13, 14, 15, 16, 99
];

export const MEDELLIN_CORREGIMIENTO_IDS = [50, 60, 70, 80, 90];

export const MEDELLIN_TERRITORY_IDS = [
  ...MEDELLIN_COMMUNE_IDS,
  ...MEDELLIN_CORREGIMIENTO_IDS
];

function toPositiveInteger(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function getMeetingGroupId(record) {
  return toPositiveInteger(
    record?.meeting_group_id ?? record?.meetingGroupId ?? record?.meetingGroup?.id
  );
}

function getMeetingGroupGroupId(record) {
  return toPositiveInteger(
    record?.groupId ?? record?.group_id ?? record?.group?.id
  );
}

function getGroupSchoolCode(group, schoolCodeFallback = '') {
  const fallbackCode =
    schoolCodeFallback && typeof schoolCodeFallback === 'object'
      ? schoolCodeFallback?.code
      : schoolCodeFallback;

  return String(group?.school?.code ?? fallbackCode ?? '').trim();
}

function getSchoolType(school) {
  return String(school?.type ?? school?.schoolType ?? school?.school_type ?? '').trim().toUpperCase();
}

export function getSchoolProgramScope(school) {
  const normalized = String(
    school?.programScope ?? school?.program_scope ?? ''
  ).trim().toUpperCase();

  return ['PF', 'NDN', 'DIPLOM', 'CS', 'COP'].includes(normalized) ? normalized : '';
}

function getUserRoleId(user) {
  return toPositiveInteger(user?.roleId ?? user?.role_id ?? user?.role?.id);
}

function getRecordMediatorRoleId(record) {
  return toPositiveInteger(
    record?.mediatorRoleId ??
      record?.mediator_role_id ??
      record?.userRoleId ??
      record?.user_role_id
  ) ||
    getUserRoleId(record?.user) ||
    getUserRoleId(record?.school?.user) ||
    getUserRoleId(record?.group?.user) ||
    getUserRoleId(record?.group?.school?.user);
}

function isProvisionalSchoolCode(code) {
  return /^IE\d+$/i.test(String(code || '').trim());
}

function matchesProvisionalRecordByAdminScope(record, scope, code) {
  if (!isProvisionalSchoolCode(code)) {
    return null;
  }

  if (scope === ADMIN_SCOPE_VALUES.COP) {
    return false;
  }

  const mediatorRoleId = getRecordMediatorRoleId(record);
  if (!mediatorRoleId) {
    return false;
  }

  if (scope === ADMIN_SCOPE_VALUES.NDN) {
    return mediatorRoleId === NDN_MEDIATOR_ROLE_ID;
  }

  if (scope === ADMIN_SCOPE_VALUES.CS) {
    return false;
  }

  if (scope === ADMIN_SCOPE_VALUES.DIPLOM) {
    return false;
  }

  if (scope === ADMIN_SCOPE_VALUES.PF) {
    return mediatorRoleId !== NDN_MEDIATOR_ROLE_ID;
  }

  return true;
}

function isCopSchoolCode(code) {
  const normalizedCode = String(code || '').trim().toUpperCase();
  return normalizedCode.startsWith('ASES') || normalizedCode.includes('COP');
}

export function isCopMeetingType(type) {
  return COP_MEETING_TYPES.includes(String(type || '').trim().toUpperCase());
}

function getGroupSchoolId(group) {
  return toPositiveInteger(
    group?.school?.id ?? group?.schoolId ?? group?.school_id
  );
}

function getMeetingSchoolId(record) {
  return toPositiveInteger(
    record?.school?.id ??
      record?.schoolId ??
      record?.school_id ??
      record?.group?.school?.id ??
      record?.group?.schoolId ??
      record?.group?.school_id
  );
}

function getMeetingSchoolCode(record) {
  return String(
    record?.schoolCode ??
      record?.school_code ??
      record?.school?.code ??
      record?.group?.school?.code ??
      ''
  ).trim();
}

export function resolveSchoolMunicipalityId(school) {
  return toPositiveInteger(
    school?.municipalityId ??
      school?.municipality_id ??
      school?.municipality?.id ??
      school?.municipality?.municipalityId ??
      school?.municipality?.municipality_id
  );
}

export function resolveGroupMunicipalityId(group, schoolFallback = null) {
  return toPositiveInteger(
    group?.municipalityId ??
      group?.municipality_id ??
      group?.school?.municipalityId ??
      group?.school?.municipality_id ??
      group?.school?.municipality?.id ??
      group?.school?.municipality?.municipalityId ??
      group?.school?.municipality?.municipality_id ??
      resolveSchoolMunicipalityId(schoolFallback)
  );
}

export function isMedellinTerritoryId(value) {
  const territoryId = toPositiveInteger(value);
  return territoryId ? MEDELLIN_TERRITORY_IDS.includes(territoryId) : false;
}

function filterCountMapByIds(countMap, allowedIds) {
  if (!countMap || typeof countMap !== 'object') {
    return {};
  }

  return Object.entries(countMap).reduce((nextMap, [id, total]) => {
    const normalizedId = toPositiveInteger(id);
    if (normalizedId && allowedIds.has(normalizedId)) {
      nextMap[normalizedId] = total;
    }
    return nextMap;
  }, {});
}

export function normalizeAdminScope(value) {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === ADMIN_SCOPE_VALUES.GENERAL) {
    return ADMIN_SCOPE_VALUES.GENERAL;
  }

  if (normalized === ADMIN_SCOPE_VALUES.PF) {
    return ADMIN_SCOPE_VALUES.PF;
  }

  if (normalized === ADMIN_SCOPE_VALUES.DIPLOM) {
    return ADMIN_SCOPE_VALUES.DIPLOM;
  }

  if (normalized === ADMIN_SCOPE_VALUES.NDN) {
    return ADMIN_SCOPE_VALUES.NDN;
  }

  if (normalized === ADMIN_SCOPE_VALUES.CS) {
    return ADMIN_SCOPE_VALUES.CS;
  }

  if (normalized === ADMIN_SCOPE_VALUES.COP) {
    return ADMIN_SCOPE_VALUES.COP;
  }

  return '';
}

export function buildAdminPathWithScope(path, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  if (!normalizedScope) {
    return path;
  }

  const [pathname, search = ''] = String(path || '').split('?');
  const searchParams = new URLSearchParams(search);
  searchParams.set('scope', normalizedScope);

  return `${pathname}?${searchParams.toString()}`;
}

export function matchesSchoolCodeByAdminScope(code, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  if (!normalizedScope) {
    return true;
  }

  const normalizedCode = String(code || '').trim().toUpperCase();
  if (!normalizedCode) {
    return false;
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.GENERAL) {
    return (
      (normalizedCode.includes('PF') ||
        normalizedCode.includes('NDN') ||
        /^C\d+DI\d+$/.test(normalizedCode)) &&
      !normalizedCode.includes('CS') &&
      !isCopSchoolCode(normalizedCode)
    );
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.NDN) {
    return normalizedCode.includes('NDN');
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.DIPLOM) {
    return /^C\d+DI\d+$/.test(normalizedCode) || normalizedCode.includes('DIPLOM');
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.CS) {
    return normalizedCode.includes('CS');
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.COP) {
    return isCopSchoolCode(normalizedCode);
  }

  return (
    normalizedCode.includes('PF') &&
    !normalizedCode.includes('NDN') &&
    !isCopSchoolCode(normalizedCode)
  );
}

export function matchesSchoolByAdminScope(school, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  if (!normalizedScope) {
    return true;
  }

  const programScope = getSchoolProgramScope(school);
  if (programScope) {
    if (normalizedScope === ADMIN_SCOPE_VALUES.GENERAL) {
      return programScope === 'PF' || programScope === 'NDN' || programScope === 'DIPLOM';
    }
    if (normalizedScope === ADMIN_SCOPE_VALUES.NDN) return programScope === 'NDN';
    if (normalizedScope === ADMIN_SCOPE_VALUES.DIPLOM) return programScope === 'DIPLOM';
    if (normalizedScope === ADMIN_SCOPE_VALUES.CS) return programScope === 'CS';
    if (normalizedScope === ADMIN_SCOPE_VALUES.COP) return programScope === 'COP';
    if (normalizedScope === ADMIN_SCOPE_VALUES.PF) return programScope === 'PF';
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.COP) {
    return (
      getSchoolType(school) === COP_SCHOOL_TYPE ||
      matchesSchoolCodeByAdminScope(school?.code, normalizedScope)
    );
  }

  if (getSchoolType(school) === COP_SCHOOL_TYPE || isCopSchoolCode(school?.code)) {
    return false;
  }

  return matchesSchoolCodeByAdminScope(school?.code, normalizedScope);
}

export function matchesMeetingGroupByAdminScope(meetingGroup, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  if (!normalizedScope) {
    return true;
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.COP) {
    return (
      isCopMeetingType(meetingGroup?.type) ||
      matchesSchoolByAdminScope(meetingGroup?.school, normalizedScope) ||
      matchesSchoolCodeByAdminScope(getMeetingSchoolCode(meetingGroup), normalizedScope)
    );
  }

  if (isCopMeetingType(meetingGroup?.type)) {
    return false;
  }

  const school = meetingGroup?.school ?? meetingGroup?.group?.school ?? null;
  if (school) {
    return matchesSchoolByAdminScope(school, normalizedScope);
  }

  return matchesSchoolCodeByAdminScope(getMeetingSchoolCode(meetingGroup), normalizedScope);
}

export function filterGroupsByAdminScope(groups, scope, schoolCodeFallback = '') {
  const normalizedScope = normalizeAdminScope(scope);
  const normalizedGroups = Array.isArray(groups) ? groups : [];

  if (!normalizedScope) {
    return normalizedGroups;
  }

  return normalizedGroups.filter(group => {
    const schoolFallback =
      schoolCodeFallback && typeof schoolCodeFallback === 'object'
        ? schoolCodeFallback
        : {};
    const groupSchool = group?.school
      ? group.school
      : {
          ...schoolFallback,
          code: getGroupSchoolCode(group, schoolCodeFallback),
          user: group?.user ?? schoolFallback?.user
        };

    if (!matchesSchoolByAdminScope(groupSchool, normalizedScope)) {
      return false;
    }

    return true;
  });
}

export function buildScopedSchools(schools, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  const normalizedSchools = Array.isArray(schools) ? schools : [];

  return normalizedSchools
    .filter(school => matchesSchoolByAdminScope(school, normalizedScope))
    .map(school => {
      const allGroups = Array.isArray(school?.groups) ? school.groups : [];

      return {
        ...school,
        allGroups,
        groups: filterGroupsByAdminScope(allGroups, normalizedScope, school)
      };
    });
}

export function isConfirmedSchool(school) {
  return school?.active !== false;
}

export function filterConfirmedSchools(schools) {
  return (Array.isArray(schools) ? schools : []).filter(isConfirmedSchool);
}

export function buildScopedGroupEntries(schools, scope) {
  return buildScopedSchools(schools, scope).flatMap(school => {
    const groups = Array.isArray(school?.groups) ? school.groups : [];

    return groups.map(group => ({
      group,
      school
    }));
  });
}

export function matchesBeneficiaryByAdminScope(beneficiary, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  if (!normalizedScope) {
    return true;
  }

  if (
    normalizedScope === ADMIN_SCOPE_VALUES.CS &&
    (
      beneficiary?.activityGroup ||
      toPositiveInteger(
        beneficiary?.activityGroupId ?? beneficiary?.activity_group_id
      )
    )
  ) {
    return true;
  }

  if (!matchesSchoolByAdminScope(beneficiary?.school, normalizedScope)) {
    return false;
  }

  return true;
}

export function matchesEvidenceByAdminScope(evidence, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  if (!normalizedScope) {
    return true;
  }

  if (String(evidence?.sourceType || '').trim().toLowerCase() === 'incidence_action') {
    return normalizedScope === ADMIN_SCOPE_VALUES.GENERAL;
  }

  const programScope = getSchoolProgramScope(evidence);
  if (programScope) {
    if (normalizedScope === ADMIN_SCOPE_VALUES.NDN) return programScope === 'NDN';
    if (normalizedScope === ADMIN_SCOPE_VALUES.DIPLOM) return programScope === 'DIPLOM';
    if (normalizedScope === ADMIN_SCOPE_VALUES.CS) return programScope === 'CS';
    if (normalizedScope === ADMIN_SCOPE_VALUES.COP) return programScope === 'COP';
    if (normalizedScope === ADMIN_SCOPE_VALUES.PF) return programScope === 'PF';
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.COP) {
    return (
      isCopMeetingType(evidence?.type) ||
      matchesSchoolCodeByAdminScope(evidence?.schoolCode, normalizedScope)
    );
  }

  const provisionalMatch = matchesProvisionalRecordByAdminScope(
    evidence,
    normalizedScope,
    evidence?.schoolCode
  );
  if (provisionalMatch !== null) {
    return provisionalMatch;
  }

  if (
    isCopMeetingType(evidence?.type) ||
    !matchesSchoolCodeByAdminScope(evidence?.schoolCode, normalizedScope)
  ) {
    return false;
  }

  return true;
}

export function filterAttendancesByAdminScope(attendances, scope) {
  const normalizedScope = normalizeAdminScope(scope);
  if (!normalizedScope || !attendances || typeof attendances !== 'object') {
    return attendances;
  }

  const groups = Array.isArray(attendances?.groups) ? attendances.groups : [];
  const meetingGroups = Array.isArray(attendances?.meetingGroupsTable)
    ? attendances.meetingGroupsTable
    : Array.isArray(attendances?.meetings)
      ? attendances.meetings
      : [];

  if (normalizedScope === ADMIN_SCOPE_VALUES.CS) {
    const scopedMeetingGroups = meetingGroups.filter(
      meetingGroup => Boolean(meetingGroup?.activityGroup)
    );
    const allowedMeetingGroupIds = new Set(
      scopedMeetingGroups
        .map(meetingGroup => toPositiveInteger(meetingGroup?.id))
        .filter(Boolean)
    );

    return {
      ...attendances,
      groups: scopedMeetingGroups
        .map(meetingGroup => meetingGroup?.activityGroup)
        .filter(Boolean),
      meetings: scopedMeetingGroups,
      meetingGroupsTable: scopedMeetingGroups,
      attendancesModel: (
        Array.isArray(attendances?.attendancesModel)
          ? attendances.attendancesModel
          : []
      ).filter(attendance =>
        allowedMeetingGroupIds.has(getMeetingGroupId(attendance))
      )
    };
  }

  if (normalizedScope === ADMIN_SCOPE_VALUES.COP) {
    const allowedGroups = groups.filter(group =>
      matchesSchoolByAdminScope(group?.school, normalizedScope)
    );
    const allowedGroupIds = new Set(
      allowedGroups
        .map(group => toPositiveInteger(group?.id))
        .filter(Boolean)
    );
    const scopedMeetingGroups = meetingGroups.filter(meetingGroup =>
      matchesMeetingGroupByAdminScope(meetingGroup, normalizedScope)
    );
    const allowedMeetingGroupIds = new Set(
      scopedMeetingGroups
        .map(meetingGroup => toPositiveInteger(meetingGroup?.id))
        .filter(Boolean)
    );
    const allowedSchoolIds = new Set(
      [
        ...allowedGroups.map(group => getGroupSchoolId(group)),
        ...scopedMeetingGroups.map(meetingGroup => getMeetingSchoolId(meetingGroup))
      ].filter(Boolean)
    );
    const scopedAttendancesModel = (
      Array.isArray(attendances?.attendancesModel) ? attendances.attendancesModel : []
    ).filter(attendance => allowedMeetingGroupIds.has(getMeetingGroupId(attendance)));

    return {
      ...attendances,
      groups: allowedGroups,
      meetings: scopedMeetingGroups,
      meetingGroupsTable: scopedMeetingGroups,
      attendancesModel: scopedAttendancesModel,
      groupBeneficiaryCounts: filterCountMapByIds(
        attendances?.groupBeneficiaryCounts,
        allowedGroupIds
      ),
      schoolBeneficiaryCounts: filterCountMapByIds(
        attendances?.schoolBeneficiaryCounts,
        allowedSchoolIds
      )
    };
  }

  const allowedGroups = groups.filter(group => {
    if (!matchesSchoolByAdminScope(group?.school, normalizedScope)) {
      return false;
    }

    return true;
  });

  const allowedGroupIds = new Set(
    allowedGroups
      .map(group => toPositiveInteger(group?.id))
      .filter(Boolean)
  );

  const allowedSchoolIds = new Set(
    allowedGroups
      .map(group => getGroupSchoolId(group))
      .filter(Boolean)
  );

  const scopedMeetingGroups = meetingGroups.filter(meetingGroup =>
    allowedGroupIds.has(getMeetingGroupGroupId(meetingGroup))
  );

  const allowedMeetingGroupIds = new Set(
    scopedMeetingGroups
      .map(meetingGroup => toPositiveInteger(meetingGroup?.id))
      .filter(Boolean)
  );

  const scopedAttendancesModel = (
    Array.isArray(attendances?.attendancesModel) ? attendances.attendancesModel : []
  ).filter(attendance => allowedMeetingGroupIds.has(getMeetingGroupId(attendance)));

  return {
    ...attendances,
    groups: allowedGroups,
    meetings: scopedMeetingGroups,
    meetingGroupsTable: scopedMeetingGroups,
    attendancesModel: scopedAttendancesModel,
    groupBeneficiaryCounts: filterCountMapByIds(
      attendances?.groupBeneficiaryCounts,
      allowedGroupIds
    ),
    schoolBeneficiaryCounts: filterCountMapByIds(
      attendances?.schoolBeneficiaryCounts,
      allowedSchoolIds
    )
  };
}
